import React, { useState, useEffect, useCallback } from "react";
import { JsonRpcProvider, Contract, parseEther, formatUnits, ZeroAddress } from "ethers";
import { launchedCoins as fallbackCoins, CHAIN } from "../mock";
import { API, NET, FLAP_NET, PORTAL_ABI, explorerAddr } from "../config";
import { useWallet } from "../context/WalletContext";
import { WavyRule } from "./Guilloche";
import { toast } from "sonner";
import { ExternalLink, TrendingUp, TrendingDown, ShoppingCart, Loader2, Wallet } from "lucide-react";

const FILTERS = ["Hot", "New", "Live", "Gainers"];
const avatar = (s) => `https://api.dicebear.com/7.x/shapes/svg?seed=${encodeURIComponent(s)}&backgroundType=gradientLinear`;

export default function LaunchBoard() {
  const { address, connect, chainOk, getProvider } = useWallet();
  const [filter, setFilter] = useState("Hot");
  const [seed, setSeed] = useState(fallbackCoins);
  const [live, setLive] = useState([]);        // real launched tokens w/ on-chain state
  const [buyIdx, setBuyIdx] = useState(null);
  const [buyAmt, setBuyAmt] = useState("");
  const [buying, setBuying] = useState(false);

  const readProvider = useCallback(() => new JsonRpcProvider(NET.rpc[0]), []);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/launches`).then((r) => r.json()).then((d) => { if (alive && d?.items?.length) setSeed(d.items); }).catch(() => {});
    (async () => {
      try {
        const res = await fetch(`${API}/tokens`);
        const toks = await res.json();
        if (!Array.isArray(toks) || !toks.length) return;
        const provider = readProvider();
        const portal = new Contract(FLAP_NET.portal, PORTAL_ABI, provider);
        const enriched = await Promise.all(toks.map(async (t) => {
          try {
            const s = await portal.getTokenV6(t.address);
            const price = Number(formatUnits(s.price, 18));
            const progress = Math.min(100, Number(s.progress) / 1e16); // 1e18 = 100%
            const graduated = Number(s.status) === 4;
            return { ...t, price, progress, graduated, live: true };
          } catch (e) {
            return { ...t, price: null, progress: 0, live: true };
          }
        }));
        if (alive) setLive(enriched);
      } catch (e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [readProvider]);

  const doBuy = async (token) => {
    if (!address) { await connect(); return; }
    if (!buyAmt || Number(buyAmt) <= 0) { toast.error("Enter a BNB amount"); return; }
    setBuying(true);
    try {
      const provider = getProvider();
      const signer = await provider.getSigner();
      const portal = new Contract(FLAP_NET.portal, PORTAL_ABI, signer);
      const value = parseEther(String(buyAmt));
      toast("Confirm the buy in your wallet\u2026");
      const tx = await portal.swapExactInput(
        { inputToken: ZeroAddress, outputToken: token, inputAmount: value, minOutputAmount: 0n, permitData: "0x" },
        { value }
      );
      toast("Broadcasting\u2026", { description: tx.hash.slice(0, 18) + "\u2026" });
      await tx.wait();
      toast.success("Bought", { description: `${buyAmt} ${NET.symbol} spent` });
      setBuyIdx(null); setBuyAmt("");
    } catch (e) {
      toast.error("Buy failed", { description: e?.shortMessage || e?.reason || e?.message || "Rejected" });
    } finally {
      setBuying(false);
    }
  };

  const seedView = filter === "Gainers" ? seed.filter((c) => c.up) : seed;
  const showLive = live.length > 0 && (filter === "Hot" || filter === "New" || filter === "Live");

  return (
    <section id="launched" className="container-x py-10 md:py-14">
      <div className="paper paper-grain rounded-sm p-7 md:p-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 className="font-number font-semibold" style={{ fontSize: "clamp(1.6rem,3.4vw,2.3rem)" }}>Trending</h2>
          <a href="#launch" className="inline-flex items-center gap-2 h-9 px-4 nav-link" style={{ background: "var(--gold)", color: "#0D0E0F", fontSize: 12 }}>Launch a coin</a>
        </div>
        <WavyRule className="my-6" />
        <p className="text-[15px] max-w-2xl mb-6" style={{ color: "var(--muted)" }}>
          Coins launched here trade on the <a href={CHAIN.launchpadUrl} target="_blank" rel="noreferrer" className="gold-text link-underline">flap.sh</a> bonding curve. Live rows show real price and curve progress read from the Portal contract — buy in one click.
        </p>

        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} className="shrink-0 px-3.5 h-8 label transition-colors" style={{ background: filter === f ? "var(--gold)" : "var(--bg-2)", color: filter === f ? "#0D0E0F" : "var(--muted)", border: filter === f ? "none" : "1px solid var(--border)" }}>{f}</button>
          ))}
        </div>

        <div className="rounded-sm overflow-hidden" style={{ border: "1px solid var(--border)" }}>
          <div className="hidden md:grid grid-cols-[2.2fr_1.1fr_1fr_1.4fr_0.9fr] px-5 py-3 label" style={{ borderBottom: "1px solid var(--border)", background: "var(--bg-2)" }}>
            <div>Token</div><div>Price</div><div>Quote</div><div>Curve / Market cap</div><div className="text-right">Trade</div>
          </div>

          {/* LIVE tokens */}
          {showLive && live.map((c, i) => (
            <div key={`live-${i}`} style={{ borderBottom: "1px solid var(--border)" }}>
              <div className="grid grid-cols-2 md:grid-cols-[2.2fr_1.1fr_1fr_1.4fr_0.9fr] px-5 py-3.5 items-center">
                <a href={`${FLAP_NET.tokenPage}/${c.address}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 min-w-0">
                  <img src={avatar(c.symbol)} alt={c.symbol} className="h-9 w-9 rounded-full" style={{ border: "1px solid var(--border)" }} />
                  <div className="min-w-0">
                    <div className="font-mono font-semibold text-[13.5px] flex items-center gap-2">${c.symbol}
                      <span className="label" style={{ fontSize: 9, color: c.graduated ? "var(--green)" : "var(--gold)" }}>{c.graduated ? "DEX" : "LIVE"}</span>
                      {c.is_tax && <span className="label" style={{ fontSize: 9 }}>tax</span>}
                    </div>
                    <div className="text-[12px] truncate" style={{ color: "var(--muted)" }}>{c.name}</div>
                  </div>
                </a>
                <div className="font-mono text-[13px] text-right md:text-left">{c.price != null ? `${c.price.toPrecision(3)}` : "—"}</div>
                <div className="hidden md:flex items-center gap-1.5 font-mono text-[13px]"><span className="h-4 w-4 rounded-full grid place-items-center text-[9px] font-bold" style={{ background: "rgba(240,185,11,0.15)", color: "#F0B90B" }}>B</span>{NET.symbol}</div>
                <div className="hidden md:block">
                  <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: "var(--card-2)" }}>
                    <div className="h-full rounded-full" style={{ width: `${c.progress || 0}%`, background: "var(--gold)" }} />
                  </div>
                  <div className="label mt-1" style={{ fontSize: 9 }}>{(c.progress || 0).toFixed(1)}% to DEX</div>
                </div>
                <div className="col-span-2 md:col-span-1 flex md:justify-end mt-2 md:mt-0">
                  <button onClick={() => { setBuyIdx(buyIdx === i ? null : i); setBuyAmt(""); }} className="inline-flex items-center gap-1.5 h-8 px-3 nav-link" style={{ background: "var(--gold)", color: "#0D0E0F", fontSize: 11 }}>
                    <ShoppingCart size={13} /> Buy
                  </button>
                </div>
              </div>
              {buyIdx === i && (
                <div className="px-5 pb-4 flex flex-wrap items-center gap-2 fade-up">
                  <input value={buyAmt} onChange={(e) => setBuyAmt(e.target.value.replace(/[^0-9.]/g, ""))} placeholder={`Amount in ${NET.symbol}`} className="h-10 px-3 font-mono text-[13px] outline-none" style={{ background: "var(--bg-2)", border: "1px solid var(--border)", color: "var(--text)", width: 200 }} />
                  {!address ? (
                    <button onClick={connect} className="inline-flex items-center gap-2 h-10 px-4 nav-link" style={{ background: "var(--red)", color: "#fff", fontSize: 11 }}><Wallet size={13} /> Connect</button>
                  ) : (
                    <button onClick={() => doBuy(c.address)} disabled={buying || !chainOk} className="inline-flex items-center gap-2 h-10 px-4 nav-link disabled:opacity-40" style={{ background: "var(--gold)", color: "#0D0E0F", fontSize: 11 }}>
                      {buying ? <Loader2 size={13} className="animate-spin" /> : <ShoppingCart size={13} />} Buy ${c.symbol}
                    </button>
                  )}
                  <a href={explorerAddr(c.address)} target="_blank" rel="noreferrer" className="label link-underline" style={{ color: "var(--muted)" }}>contract <ExternalLink size={10} className="inline" /></a>
                </div>
              )}
            </div>
          ))}

          {/* SEED tokens (browse) */}
          {seedView.map((c, i) => (
            <a key={`seed-${i}`} href={CHAIN.launchpadUrl} target="_blank" rel="noreferrer"
               className="grid grid-cols-2 md:grid-cols-[2.2fr_1.1fr_1fr_1.4fr_0.9fr] px-5 py-3.5 items-center transition-colors"
               style={{ borderBottom: i < seedView.length - 1 ? "1px solid var(--border)" : "none" }}
               onMouseEnter={(e) => (e.currentTarget.style.background = "var(--card-2)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              <div className="flex items-center gap-3 min-w-0">
                <img src={c.img} alt={c.symbol} className="h-9 w-9 rounded-full" style={{ border: "1px solid var(--border)" }} />
                <div className="min-w-0">
                  <div className="font-mono font-semibold text-[13.5px] flex items-center gap-2">${c.symbol} <span className="label" style={{ fontSize: 9 }}>{c.age}</span></div>
                  <div className="text-[12px] truncate" style={{ color: "var(--muted)" }}>{c.name}</div>
                </div>
              </div>
              <div className="font-mono text-[13px] text-right md:text-left">{c.price}</div>
              <div className="hidden md:flex items-center gap-1.5 font-mono text-[13px]"><span className="h-4 w-4 rounded-full grid place-items-center text-[9px] font-bold" style={{ background: "rgba(240,185,11,0.15)", color: "#F0B90B" }}>{c.quote[0]}</span>{c.quote}</div>
              <div className="hidden md:block font-mono text-[13px]">{c.mc}</div>
              <div className="col-span-2 md:col-span-1 flex md:justify-end items-center gap-1 font-mono text-[13px] mt-1 md:mt-0" style={{ color: c.up ? "var(--green)" : "var(--red)" }}>
                {c.up ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{c.change}
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
